import { Controller } from "react-hook-form";

import { FormField } from "components/form/FormField/FormField";
import { InfoTooltip } from "components/ui/InfoTooltip";
import { Select } from "components/ui/Select/Select";
import {
  FAMILY_HISTORY_CHECK_INFO,
  FAMILY_HISTORY_CHECK_LABEL,
  PATIENT_CHECK_OPTIONS,
} from "features/pregnancy/utils/patientCheckAnswers";

import style from "./AddPregnancyForm.module.css";

/*
 * The four patient checks, shared by the add and the edit form so the pregnancy cannot read one way
 * on the way in and another on the way out (KIN-2056).
 *
 * None of the four is an MN-CMS field — no SCT or LOINC code, no OBX, nothing in the outbound
 * message depends on them — so the control is a product decision, not a spec one. They were tick
 * boxes, and Darren could not tell what a tick asserted: "its unclear if you are ticking to say yes
 * or no." A dropdown of the three real answers says it outright, and matches both the patient
 * record, which already offers smoking as a yes / no / unknown select, and the urinalysis fields on
 * the antenatal visit, which gave up their lone tick box for the same reason.
 *
 * The family-history label is a rewording. It was "No family history of congenital abnormality",
 * which a tick could answer but Yes and No could not — "No" to it is a double negative that reads
 * as the opposite of what it means. The stored value keeps its meaning exactly: true is still the
 * GP confirming there is no such family history, which is what ticking the old box said, so nothing
 * is inverted and no record changes meaning. See patientCheckAnswers for why the direction must
 * stay as it is.
 *
 * It is also the only one of the four that needs a tooltip: shortened to fit a single line it no
 * longer names the abnormality, and it is the only label whose answers are worth spelling out.
 */
const PATIENT_CHECKS = [
  { name: "hasClinicHCGPositive", label: "Clinic HCG positive" },
  { name: "smoker", label: "Smoker" },
  {
    name: "noFamilyHistoryOfCongenitalAbnormality",
    label: FAMILY_HISTORY_CHECK_LABEL,
    info: FAMILY_HISTORY_CHECK_INFO,
  },
  { name: "hasHadChickenPox", label: "Has had chicken pox" },
];

export const PatientChecksFields = ({ control }) => (
  <div className={style.inputGroupGrid4}>
    {PATIENT_CHECKS.map(({ name, label, info }) => (
      <FormField key={name} name={name}>
        <label className={style.pregnancyFormLabel}>
          {label}
          {!!info && <InfoTooltip text={info} />}
        </label>
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            /* No placeholder: every answer including "Not asked" is a real option, so the select
               has no fourth blank state that would be indistinguishable from not asking. */
            <Select
              name={name}
              options={PATIENT_CHECK_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              placeholder=""
              fullWidth
            />
          )}
        />
      </FormField>
    ))}
  </div>
);
